import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useFieldArray, useForm, useWatch } from 'react-hook-form';
import { useDispatch } from 'react-redux';

import AutoComplete from 'components/field/AutoComplete';
import Button from 'components/field/Button';
import EnumSelect from 'components/field/EnumSelect';
import IntegerInput from 'components/field/IntegerInput';
import StringInput from 'components/field/StringInput';
import SwitchInput from 'components/field/SwitchInput';
import TextArea from 'components/field/TextArea';
import Form from 'components/form/Form';
import FormItem from 'components/form/FormItem';
import Modal from 'components/global/Modal';
import Typography from 'components/global/Typography';
import { ModalActions, ModalContent, ModalTitle } from 'components/global/styled/Modal.styled';
import { updateDynamicListData } from 'components/list/dynamicListSlice';
import Table from 'components/table/Table';

import useAlert from 'hooks/useAlert';
import useAxiosPrivate from 'hooks/useAxiosPrivate';
import useModalRouter from 'hooks/useModalRouter';
import { usePrompt } from 'hooks/usePrompt';

import { AUTOCOMPLETE, ENUMS, WMS_MODULE } from 'services/consts';

import AppLogger from 'utils/AppLogger';
import { DYNAMICLISTS } from 'utils/constants';

import AttributesGroupForm from '../AttributesGroups/AttributesGroupForm';

const defaultColSpan = {
	md: 6
};

const ProductAttributesForm = NiceModal.create(({ id, mode, modalName }) => {
	const [isLoading, setIsLoading] = useState(false);
	const modal = useModal();
	const axiosPrivate = useAxiosPrivate();
	const dispatch = useDispatch();
	const { closeModal } = useModalRouter();

	const { showSuccess, showError } = useAlert();

	const columns = useMemo(
		() => [
			{
				header: 'label',
				accessorKey: 'label',
				enableSorting: false,
				size: 100,
				renderCell: ({ row }) => (
					<StringInput
						name={`options[${row.index}].label`}
						disabled={viewMode}
						value={watch(`products[${row.index}].label`)}
						setValue={(value) => {
							setValue(`products[${row.index}].label`, value, {
								shouldValidate: true,
								shouldDirty: true
							});
						}}
					/>
				),
				showOnMobile: true
			},
			{
				header: 'value',
				accessorKey: 'value',
				enableSorting: false,
				size: 100,
				renderCell: ({ row }) => (
					<StringInput
						name={`options[${row.index}].value`}
						disabled={viewMode}
						value={watch(`products[${row.index}].value`)}
						setValue={(value) => {
							setValue(`products[${row.index}].value`, value, {
								shouldValidate: true,
								shouldDirty: true
							});
						}}
					/>
				),
				showOnMobile: true
			},
			...(mode === 'edit' || mode === 'add'
				? [
						{
							id: 'deleteRow',
							size: 30,
							maxSize: 30,
							showOnMobile: true
						}
					]
				: [])
		],
		[]
	);

	const schema = useMemo(
		() =>
			yup
				.object()
				.shape({
					// product Details
					name: yup.string().nullable().required(),
					label: yup.string().nullable().required(),
					type: yup.string().nullable().required(),
					description: yup.string().nullable(),
					defaultValue: yup.string().nullable(),
					groupID: yup.number().nullable(),
					showOnImport: yup.boolean().nullable().required(),
					options: yup
						.array()
						.of(
							yup.object().shape({
								label: yup.string().nullable().required(),
								value: yup.string().nullable().required()
							})
						)
						.nullable()
						.when('type', {
							is: (type) => type === 'Dropdown',
							then: (schema) => schema.required()
						})
				})
				.required(),
		[]
	);

	const viewMode = mode === 'view';

	const handleCloseModal = useCallback(() => {
		if (modalName) {
			closeModal();
		} else {
			modal.remove();
		}
	}, [modalName]);

	const formMethods = useForm({
		resolver: yupResolver(schema),
		defaultValues: {
			name: null,
			label: null,
			type: null,
			description: null,
			defaultValue: null,
			groupID: null,
			options: null,
			showOnImport: false
		}
	});

	const {
		handleSubmit,
		setValue,
		watch,
		reset,
		control,
		formState: { isDirty, isSubmitting, isSubmitSuccessful }
	} = formMethods;

	const optionsWatcher = useWatch({ control, name: 'options' });
	const typeWatcher = useWatch({ control, name: 'type' });

	const { fields, append, remove, move } = useFieldArray({
		name: 'options',
		control
	});

	const controlledFields = fields.map((field, index) => {
		if (!optionsWatcher) return field;
		return {
			...field,
			...optionsWatcher[index]
		};
	});

	usePrompt(isDirty && !isSubmitting && !isSubmitSuccessful);

	const fillForm = useCallback(() => {
		if (id) {
			setIsLoading(true);
			axiosPrivate
				.post(WMS_MODULE.GET_PRODUCT_ATTRIBUTE, {
					ID: id
				})
				.then((response) => {
					const { data, status } = response;

					if (status === 200) {
						// reset form fields
						reset({
							name: data.attributeName ?? null,
							label: data.attributeLabel ?? null,
							type: data.type ?? null,
							description: data.description ?? null,
							defaultValue: data.defaultValue ?? null,
							groupID: data.groupID ?? null,
							options: data.options ?? null,
							showOnImport: data.showOnImport ?? false
						});
					} else AppLogger.error(response);
				})
				.catch((error) => {
					showError(error);
					AppLogger.error(error);
				})
				.finally(() => setIsLoading(false));
		}
	}, [id]);

	useEffect(() => {
		if (id) fillForm();
	}, [id]);

	const onSubmit = (data) => {
		const dataWithPosition = { ...data, ID: id };
		if (data.defaultValue && (data.type === 'Dropdown' || data.type === 'Date'))
			dataWithPosition.defaultValue = null;
		setIsLoading(true);
		if (data && data.options && Array.isArray(data.options)) {
			dataWithPosition.options = data.options.map((option, index) => ({ ...option, position: index }));
		}

		const URL = id ? WMS_MODULE.UPDATE_PRODUCT_ATTRIBUTE : WMS_MODULE.ADD_PRODUCT_ATTRIBUTE;

		axiosPrivate
			.post(URL, dataWithPosition)
			.then((response) => {
				const { status } = response;

				if (status === 200) {
					showSuccess();
					dispatch(updateDynamicListData(DYNAMICLISTS.PRODUCT_ATTRIBUTES));
					handleCloseModal();
				}
			})
			.catch((error) => {
				if (error.response.data.code === 'error.W006') {
					showError({ message: 'errorMsg.uniqueAttributeName' });
				} else {
					showError();
				}
				AppLogger.error(error);
			})
			.finally(() => setIsLoading(false));
	};

	const groupIDAutocompleteColumns = useMemo(() => [{ accessor: 'groupName', width: 200 }], []);

	const defaultValueInput = useMemo(() => {
		if (typeWatcher === 'Number' || typeWatcher === 'Integer')
			return (
				<IntegerInput
					name="defaultValue"
					disabled={viewMode}
					allowNegativeValues={true}
					allowFloat={typeWatcher === 'Number'}
				/>
			);
		else if (typeWatcher === 'Boolean')
			return (
				<SwitchInput
					className="mt-3"
					viewMode={viewMode}
					value={watch('defaultValue') === 'true'}
					setValue={(value) => {
						setValue('defaultValue', value.target.checked.toString(), {
							shouldValidate: true,
							shouldDirty: true
						});
					}}
				/>
			);
		else if (typeWatcher === 'Text') return <TextArea name="defaultValue" disabled={viewMode} />;
		else return <StringInput name="defaultValue" disabled={viewMode} />;
	}, [typeWatcher, watch('defaultValue')]);

	return (
		<Modal fullwidth maxWidth="medium" name={modalName} className="modal-product-attributes">
			<ModalTitle>
				<Typography variant="p1">
					{mode === 'edit'
						? 'editProductAttribute'
						: mode === 'view'
							? 'viewProductAttribute'
							: 'addProductAttribute'}
				</Typography>
			</ModalTitle>
			<ModalContent>
				<Form formMethods={formMethods} borderless viewPrintButton={viewMode}>
					<FormItem colSpan={defaultColSpan} label="attributeName" required isLoading={isLoading}>
						<StringInput name="name" disabled={viewMode} />
					</FormItem>
					<FormItem colSpan={defaultColSpan} label="attributeLabel" required isLoading={isLoading}>
						<StringInput name="label" disabled={viewMode} />
					</FormItem>

					<FormItem label="description" isLoading={isLoading}>
						<TextArea disabled={viewMode} name="description" />
					</FormItem>

					<FormItem
						colSpan={typeWatcher === 'Dropdown' || typeWatcher === 'Date' ? undefined : defaultColSpan}
						label="type"
						required
						isLoading={isLoading}>
						<EnumSelect
							name="type"
							disabled={viewMode}
							enumName={ENUMS.PRODUCT_ATTRIBUTE_TYPES}
							value={watch('type')}
							setValue={(value) => {
								setValue('type', value, { shouldValidate: true, shouldDirty: true });
								if (value === 'Boolean') {
									setValue('defaultValue', 'false', { shouldValidate: true, shouldDirty: true });
								}
							}}
						/>
					</FormItem>

					{typeWatcher !== 'Dropdown' && typeWatcher !== 'Date' && (
						<FormItem colSpan={defaultColSpan} label="defaultValue" isLoading={isLoading}>
							{defaultValueInput}
						</FormItem>
					)}

					<FormItem
						colSpan={defaultColSpan}
						label="showOnImport"
						isLoading={isLoading}
						description="showOnImportDescription"
						direction="row-reverse">
						<SwitchInput className="mt-3" formMode={mode} viewMode={viewMode} name="showOnImport" />
					</FormItem>

					<FormItem colSpan={defaultColSpan} label="groupName" isLoading={isLoading}>
						<AutoComplete
							name="groupID"
							disabled={viewMode}
							columns={groupIDAutocompleteColumns}
							autoCompleteName={AUTOCOMPLETE.PRODUCT_GROUP}
							setValue={(value) => {
								if (watch('groupID') !== value?.ID) {
									setValue(`groupID`, value?.ID ?? null, {
										shouldValidate: true,
										shouldDirty: true
									});
								}
							}}
							value={watch(`groupID`)}
							selectionLabelAccessor={['groupName']}
							firstItem={{
								action: () =>
									NiceModal.show(AttributesGroupForm, {
										setGroup: (value) => {
											setValue(`groupID`, value ?? null, {
												shouldValidate: true,
												shouldDirty: true
											});
										}
									}),
								label: <Typography color="var(--blue)">addANewGroup</Typography>
							}}
						/>
					</FormItem>
					{typeWatcher === 'Dropdown' && (
						<Table
							columns={columns}
							data={controlledFields}
							loading={isLoading}
							showAddNewRowButton={mode !== 'view'}
							onRowDelete={(item) => remove(item.index)}
							onRowDrag={viewMode ? undefined : move}
							onRowAdd={() => {
								append({ label: null, value: null });
							}}
						/>
					)}
				</Form>
			</ModalContent>

			{!viewMode && (
				<ModalActions>
					<Button label="cancel" variant="outline" onClick={handleCloseModal} />

					<Button
						loading={isLoading}
						onClick={handleSubmit(onSubmit, (errors) => {
							AppLogger.log(errors);
						})}
						disabled={!isDirty}
						disabledTooltip={!isDirty && 'formInfo.noChanges'}
						label={mode === 'edit' ? 'save' : 'button.add'}
					/>
				</ModalActions>
			)}
		</Modal>
	);
});

export default ProductAttributesForm;
